import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import React, { ChangeEvent, useState } from 'react';
import BaseButton from 'src/common/components/base-button';
import { LiveWeightArea } from 'src/common/components/live-weight-area/live-weight-area';
import { ILoginUser } from 'src/common/interfaces/dtos/ILoginUser';
import { ILoginUserErrors } from 'src/common/interfaces/dtos/ILoginUserErrors';
import { IShowPassword } from 'src/common/interfaces/dtos/IShowPassword';
import { IButton } from 'src/common/interfaces/IButton';
import { useTranslation } from 'react-i18next';
import { CssTextField } from 'src/css-text-field/css-text-field';
import { getRegisterTouchpointGreetings } from '../helper/touchpoint-register-helper';
import { newStyles } from './login-emerald-styles';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';

const RegisterForm: React.FC<IRegisterFormProps> = (props: IRegisterFormProps): JSX.Element => {
  const classes = newStyles();
  const { t } = useTranslation();

  const { cancelButton, submitButton } = props;
  const submitButtonLabel =
    typeof submitButton.label === 'string' ? t(submitButton.label) : submitButton.label;
  const cancelButtonLabel =
    typeof cancelButton.label === 'string' ? t(cancelButton.label) : cancelButton.label;
  const [showPassword, setShowPassword] = useState<IShowPassword>({ displayPassword: false });

  const { registrationAuthenticationSupported } = getCurrentXLRConfig();

  const handleClickShowPassword = (): void => {
    setShowPassword({
      ...showPassword,
      displayPassword: !showPassword.displayPassword,
    });
  };

  const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const id = event.target.id;
    const value = event.target.value;
    switch (id) {
      case 'userId':
        props.setUser({
          ...props.user,
          userName: value,
        });
        break;
      case 'password':
        props.setUser({
          ...props.user,
          password: value,
        });
        break;
      case 'touchPointId':
        props.setUser({
          ...props.user,
          touchPointId: value,
        });
        break;
      case 'businessUnitId':
        props.setUser({
          ...props.user,
          businessUnitId: value,
        });
        break;
    }
  };

  const checkCredential = (): boolean => {
    return !props.user.userName || !props.user.password;
  };

  const checkTouchPoint = (): boolean => {
    return !props.user.touchPointId || (props.showBuId && !props.user.businessUnitId);
  };

  const disabledRegistration = (): boolean => {
    if (registrationAuthenticationSupported) {
      return checkCredential() || checkTouchPoint();
    }
    return checkTouchPoint();
  };

  return (
    <Grid container item sm={7}>
      <Grid item container sm={8} className={classes.topIconPanel} />
      <Grid item container sm={4} justifyContent={'center'} className={classes.topIconPanel}>
        <LiveWeightArea isLeftHanded={false} isSummaryDisabled={false} isReversed={true} />
      </Grid>
      <Grid container item sm={2} />
      <Grid container item sm={8} className={classes.registerContainer}>
        <Grid container justifyContent='space-between' alignItems='center'>
          <Grid item>
            <Typography
              className={classes.typographytitle}
              display='inline'
              data-testid='register-touchpoint-greeting-title'
            >
              {t(getRegisterTouchpointGreetings())}
            </Typography>
            <Grid item xs={1}>
              <Typography className={classes.typographytitlecontainer} />
            </Grid>
          </Grid>
        </Grid>
        <form
          className={classes.registerform}
          onSubmit={props.onSubmit}
          noValidate
          data-testid='FormSubmit'
        >
          <Grid container className={classes.formError}>
            {props.errors.Description && (
              <Grid item container>
                <ReportProblemOutlinedIcon classes={{ root: `${classes.erroricon}` }} />
                <span className={classes.errorMessage} data-testid='FormErrorLabel'>
                  {props.errors.Description}
                </span>
              </Grid>
            )}
          </Grid>

          <Grid container>
            {registrationAuthenticationSupported && (
              <>
                <Grid item sm={12} className={classes.registerFieldRegistration}>
                  <CssTextField
                    classes={{ root: classes.registerFieldUnderline }}
                    variant='standard'
                    margin='none'
                    fullWidth
                    inputProps={{
                      'data-testid': 'userid-input',
                      className: 'global-keyboard',
                      'data-custombuttons': 'keyboardWithClearAndNextButton',
                    }}
                    id='userId'
                    label={t('UserIdLabel')}
                    name='userId'
                    onChange={onTextChange}
                    value={props.user.userName || ''}
                    autoComplete='off'
                    autoFocus
                  />
                </Grid>
                <Grid item sm={12} className={classes.registerFieldRegistration}>
                  <CssTextField
                    classes={{ root: classes.registerFieldUnderline }}
                    id='password'
                    variant='standard'
                    margin='none'
                    fullWidth
                    inputProps={{
                      'data-testid': 'password-input',
                      className: 'global-keyboard',
                      'data-custombuttons': 'keyboardWithClearAndNextButton',
                    }}
                    data-testid='pin-code'
                    name='password'
                    label={t('RegisterPassword')}
                    type={showPassword.displayPassword ? 'text' : 'password'}
                    onChange={onTextChange}
                    value={props.user.password || ''}
                    autoComplete='new-password'
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            className={classes.whiteiconbutton}
                            edge='end'
                            aria-label='Toggle password visibility'
                            onClick={handleClickShowPassword}
                            tabIndex={-1}
                            data-testid='toggle-password-btn'
                          >
                            {showPassword.displayPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </>
            )}

            <Grid item sm={12} className={classes.registerFieldRegistration}>
              <CssTextField
                classes={{ root: classes.registerFieldUnderline }}
                id='touchPointId'
                variant='standard'
                margin='none'
                inputProps={{
                  'data-testid': 'touchpoint-input',
                  className: 'global-keyboard',
                  'data-custombuttons': 'keyboardWithClearAndNextButton',
                }}
                data-testid='TpId'
                fullWidth
                name='touchPointId'
                label={t('TouchpointId')}
                onChange={onTextChange}
                value={props.user.touchPointId || ''}
                autoComplete='off'
              />
            </Grid>
            {props.showBuId && (
              <Grid item sm={12} className={classes.registerFieldRegistration}>
                <CssTextField
                  classes={{ root: classes.registerFieldUnderline }}
                  id='businessUnitId'
                  variant='standard'
                  margin='none'
                  inputProps={{
                    'data-testid': 'businessUnit-input',
                    className: 'global-keyboard',
                    'data-custombuttons': 'keyboardWithClearAndNextButton',
                  }}
                  data-testid='BuId'
                  fullWidth
                  name='businessUnitId'
                  label={t('BusinessUnitId_Abbrevation')}
                  onChange={onTextChange}
                  value={props.user.businessUnitId || ''}
                  autoComplete='off'
                />
              </Grid>
            )}
            <Grid item sm={6}>
              <BaseButton
                buttonType='white'
                className={`${classes.cancelbutton} ${classes.buttondefaultfont}`}
                fullWidth
                data-testid='cancel-btn'
                type='reset'
                onClick={() => cancelButton.onClick()}
                tabIndex={-1}
                disabled={cancelButton.disabled}
              >
                {cancelButtonLabel}
              </BaseButton>
            </Grid>
            <Grid item sm={6}>
              <BaseButton
                buttonType='primary'
                className={`${classes.bluebutton} ${classes.buttonwhitefont}`}
                fullWidth
                data-testid='submit-btn'
                type='submit'
                tabIndex={-1}
                disabled={disabledRegistration()}
              >
                {submitButtonLabel}
              </BaseButton>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default RegisterForm;

export interface IRegisterFormProps {
  cancelButton: IButton;
  submitButton: IButton;
  showBuId: boolean;
  errors: ILoginUserErrors;
  user: ILoginUser;
  setUser: (state: ILoginUser) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  setErrors: (state: ILoginUserErrors) => void;
}
